// TradesPanel.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import usePagedList from '../../hooks/usePagedList';
import { FeedCard, FeedSection, FeedSegments, LoadMore, PanelState } from '../feed/FeedCard';
import { TRADE_PAGE_SIZE, TRADE_WINDOWS, fetchLargestTrades } from '../../utils/spending';
import TradeRow from './TradeRow';

/**
 * TradesPanel
 *
 * The largest stock trades members of Congress reported, over a window of the
 * last 30, 90 or 365 days of trading. "Largest" is api2's order: by the bottom
 * of each reported range, then its top, with an open range ("Over
 * $50,000,000") above a closed one that starts at the same figure. It never
 * implies a single amount, and nothing here computes one.
 *
 * Each trade is a FeedItem (see TradeRow), and the law's own limit on using
 * these reports sits under the list, as the guide asks. Load more pages on
 * through the window by offset, against the response's total.
 */

const tradeKey = (trade) =>
  [trade.url, trade.member_name, trade.ticker, trade.asset, trade.tx_date, trade.tx_type, trade.amount].join('|');

function TradesPanel() {
  const { t } = useTranslation('common');
  const [days, setDays] = useState(90);
  const trades = usePagedList(
    (pageIndex) =>
      fetchLargestTrades(days, TRADE_PAGE_SIZE, pageIndex * TRADE_PAGE_SIZE).then((body) => {
        const rows = Array.isArray(body?.results) ? body.results : [];
        const total = typeof body?.total === 'number' ? body.total : null;
        return {
          rows,
          hasMore:
            total !== null
              ? (pageIndex + 1) * TRADE_PAGE_SIZE < total
              : rows.length === TRADE_PAGE_SIZE,
          meta: body,
        };
      }),
    [days],
    { keyOf: tradeKey }
  );
  const rows = trades.rows;

  return (
    <FeedSection
      id="trades"
      title={t('common:tradesTitle', 'Largest stock trades members reported')}
      subtitle={t(
        'common:tradesSubtitle',
        'Members report each trade as a value range, not an amount. Trades by a spouse or child are reported by the member.'
      )}
      aside={
        <FeedSegments
          label={t('common:tradeWindow', 'Traded in the last')}
          value={days}
          onChange={setDays}
          options={TRADE_WINDOWS.map((value) => ({
            id: value,
            label: t('common:lastDays', '{{days}} days', { days: value }),
          }))}
        />
      }
    >
      {(trades.loading || trades.error || !rows.length) && (
        <FeedCard>
          <PanelState
            loading={trades.loading}
            error={trades.error}
            empty={!trades.loading && !trades.error && !rows.length}
          />
        </FeedCard>
      )}

      {/* One FeedItem per trade, as a feed is. */}
      {!trades.loading &&
        rows.map((trade, index) => (
          <TradeRow key={`${trade.url || trade.member_name}-${trade.ticker}-${index}`} trade={trade} />
        ))}

      {!trades.loading && (
        <LoadMore
          hasMore={trades.hasMore}
          loading={trades.loadingMore}
          error={trades.moreError}
          onClick={trades.loadMore}
          label={t('common:loadMoreTrades', 'Load more trades')}
        />
      )}

      <div className="spending-notes">
        <p>
          {t(
            'common:tradesLawNote',
            'Federal law prohibits using these reports for commercial purposes other than news and communications media, to determine credit ratings, or to solicit money.'
          )}
        </p>
      </div>
    </FeedSection>
  );
}

export default TradesPanel;